/**
 * AdminRoute Component - Phase 2C-1
 * 
 * Route guard that requires admin privileges.
 * Shows 403 or redirects if user is not an admin.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export interface AdminRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  showForbidden?: boolean;
}

export function AdminRoute({
  children,
  redirectTo = '/',
  showForbidden = true,
}: AdminRouteProps): JSX.Element {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show forbidden or redirect if not admin
  if (!isAdmin) {
    if (showForbidden) {
      return <ForbiddenScreen />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Loading screen shown while checking authentication
 */
function LoadingScreen(): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4">
          <LoadingSpinner />
        </div>
        <p className="text-slate-400">Checking permissions...</p>
      </div>
    </div>
  );
}

/**
 * Forbidden screen for non-admin users
 */
function ForbiddenScreen(): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-900/20 
                        flex items-center justify-center">
          <ShieldIcon className="w-8 h-8 text-red-400" />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-100 mb-2">
          Access Denied
        </h1>
        
        <p className="text-slate-400 mb-6">
          You don't have permission to access this page. This area is restricted to administrators only.
        </p>
        
        <a
          href="/"
          className="inline-flex items-center justify-center px-4 py-2 
                     text-sm font-medium text-white bg-blue-600 rounded-lg 
                     hover:bg-blue-700 transition-colors"
        >
          Return to Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner(): JSX.Element {
  return (
    <svg
      className="animate-spin w-full h-full text-blue-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Shield icon for forbidden screen
 */
function ShieldIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z"
      />
    </svg>
  );
}

export default AdminRoute;
